import { Card, CardBody } from '@heroui/react';
import React from 'react';

import { BaseButton } from '../../BaseButton';

export interface DataTablePaginationProps {
  /**
   * Total number of items across all pages
   */
  totalItems: number;
  /**
   * Current page number (1-based)
   */
  currentPage: number;
  /**
   * Number of items per page
   */
  pageSize: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Text to display for the item type (e.g., "users", "suppliers")
   * @default "items"
   */
  itemName?: string;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Whether the data is currently loading
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether to show the card container
   * @default true
   */
  showCard?: boolean;
}

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  itemName = 'items',
  className = '',
  isLoading = false,
  showCard = true,
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const content = (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div className="text-sm text-default-600">
        <span>
          Showing{' '}
          <span className="font-medium">
            {isLoading ? '...' : `${startItem} to ${endItem}`}
          </span>{' '}
          of{' '}
          <span className="font-medium">{isLoading ? '...' : totalItems}</span>{' '}
          {itemName}
        </span>
      </div>
      <div className="flex gap-2">
        <BaseButton
          variant="bordered"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          Previous
        </BaseButton>
        <BaseButton
          variant="bordered"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
        >
          Next
        </BaseButton>
      </div>
    </div>
  );

  if (!showCard) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Card className={`mt-6 ${className}`}>
      <CardBody>{content}</CardBody>
    </Card>
  );
};
